import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Loader2, AlertCircle, ArrowLeft, Building2, RefreshCw, CheckCircle, KeyRound,
  FileText, Eye, Download, Calendar, X
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Invoices() {
  const { user, token, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/auth?mode=login');
    }
  }, [authLoading, token, navigate]);

  useEffect(() => {
    if (token) {
      fetchInvoices();
    }
  }, [token]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/api/invoices/my-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
      if (response.data.message) {
        setMessage(response.data.message);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async (e) => {
    e.preventDefault();
    if (!linkCode || linkCode.length < 4) {
      setLinkError('Please enter a valid verification code');
      return;
    }
    setLinkLoading(true);
    setLinkError('');
    try {
      const response = await axios.post(`${API}/api/auth/link-store-account`, 
        { code: linkCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (response.data.success) {
        setLinkSuccess('Store profile linked successfully!');
        setShowLinkForm(false);
        if (refreshUser) await refreshUser();
        fetchInvoices();
      }
    } catch (err) {
      setLinkError(err.response?.data?.detail || 'Failed to link account.');
    } finally {
      setLinkLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return ''; }
  };

  const getPaymentMethod = (row) => {
    if (!row.paymentMethod || row.paymentMethod === 'Cash') return 'in cash.';
    const accountName = row.paymentAccountName || row.paymentMethod;
    const memo = row.memo ? ` ${row.memo}` : '';
    return `in ${accountName}.${memo}`;
  };

  const getCurrencyName = (code) => {
    const names = { 'USD': 'Dollars', 'CAD': 'Canada Dollars', 'EUR': 'Euros', 'GBP': 'British Pounds' };
    return names[code] || code;
  };

  // Build transaction summary from rows
  const getTransactionSummary = (invoice) => {
    if (!invoice.rows || !Array.isArray(invoice.rows) || invoice.rows.length === 0) {
      return { received: [], paid: [] };
    }
    const received = invoice.rows.filter(r => {
      const val = parseFloat(r.received || r.amount_received || 0);
      return val > 0;
    });
    const paid = invoice.rows.filter(r => {
      const val = parseFloat(r.paid || r.amount_paid || 0);
      return val > 0;
    });
    return { received, paid };
  };

  // Helper to safely get amount value
  const getAmount = (row, type) => {
    if (type === 'received') {
      return parseFloat(row.received || row.amount_received || 0);
    }
    return parseFloat(row.paid || row.amount_paid || 0);
  };

  const handleDownloadPDF = (invoice) => {
    const receiptWindow = window.open('', '_blank');
    const customerName = invoice.customer_snapshot?.name || `${user?.first_name || ''} ${user?.last_name || ''}`;
    const customerId = invoice.customer_snapshot?.customer_id || user?.customer_id;
    const { received, paid } = getTransactionSummary(invoice);
    
    let transactionsHTML = '';
    received.forEach(row => {
      const amount = getAmount(row, 'received');
      const currency = row.currency || row.currency_code;
      transactionsHTML += `
        <div style="border-top: 2px solid #1a3a5c; margin: 15px 0; padding-top: 15px;">
          <p style="font-weight: bold; font-size: 16px;">Bought from customer</p>
          <p style="text-align: right; font-weight: bold; font-size: 20px; margin: 10px 0;">(${amount.toLocaleString()} ${currency})</p>
          <p style="font-size: 13px;">Rate ${row.rate || 'N/A'} &nbsp;&nbsp; Fee ${row.fee || '0.00'}</p>
          <p style="font-size: 13px;">Bought from customer ${amount.toLocaleString()} ${getCurrencyName(currency)} ${getPaymentMethod(row)}</p>
        </div>
      `;
    });
    paid.forEach(row => {
      const amount = getAmount(row, 'paid');
      const currency = row.currency || row.currency_code;
      transactionsHTML += `
        <div style="border-top: 2px solid #1a3a5c; margin: 15px 0; padding-top: 15px;">
          <p style="font-weight: bold; font-size: 16px;">Paid to customer</p>
          <p style="text-align: right; font-weight: bold; font-size: 20px; margin: 10px 0;">(${amount.toLocaleString()} ${currency})</p>
          <p style="font-size: 13px;">Paid to customer ${amount.toLocaleString()} ${getCurrencyName(currency)} ${getPaymentMethod(row)}</p>
        </div>
      `;
    });

    const receiptHTML = `
      <!DOCTYPE html><html><head><title>Receipt - ${invoice.invoice_number}</title>
      <style>body{font-family:Arial,sans-serif;max-width:400px;margin:0 auto;padding:20px;color:#1a3a5c;}</style></head>
      <body>
        <div style="text-align:center;margin-bottom:20px;">
          <img src="https://customer-assets.emergentagent.com/job_1c80a367-1819-4b8f-a04a-b9ee904ccfaa/artifacts/y1bmcq41_logo.png" style="height:50px;" />
          <p style="font-weight:bold;margin:10px 0 3px;">FonderX Money Exchange Inc.</p>
          <p style="margin:3px 0;">Unit 2 - 723 12th St., New Westminster, V3M 4J8</p>
          <p style="margin:3px 0;">(778) 759-0400</p>
        </div>
        <div style="display:flex;justify-content:space-between;padding:10px 0;border-top:3px double #1a3a5c;border-bottom:3px double #1a3a5c;">
          <div><p style="font-weight:bold;margin:3px 0;">${invoice.invoice_number}</p><p style="margin:3px 0;">${invoice.teller_name || 'Staff'}</p><p style="margin:3px 0;">${customerName}</p></div>
          <div style="text-align:right;"><p style="font-weight:bold;margin:3px 0;">${formatDate(invoice.date)}</p><p style="margin:3px 0;">${formatTime(invoice.date)}</p><p style="font-weight:bold;margin:3px 0;">${customerId}</p></div>
        </div>
        ${transactionsHTML}
        <div style="text-align:center;margin-top:20px;padding-top:15px;border-top:1px solid #ddd;">
          <span style="background:#d1fae5;color:#059669;padding:3px 12px;border-radius:12px;font-size:12px;">${invoice.status}</span>
        </div>
      </body></html>
    `;
    receiptWindow.document.write(receiptHTML);
    receiptWindow.document.close();
    receiptWindow.print();
  };

  const hasLinkedProfile = user?.customer_id;

  if (authLoading) {
    return <div className="min-h-screen bg-[#001435] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#001435]">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-blue-100/60 hover:text-white text-sm mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </Link>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">Transaction History</h1>
          </div>
          <button onClick={fetchInvoices} disabled={loading} className="self-start p-2 bg-white/5 hover:bg-white/10 rounded-lg text-blue-100/60">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* No profile linked */}
        {!hasLinkedProfile && !showLinkForm && (
          <div className="bg-white rounded-xl p-6 sm:p-8 text-center">
            <Building2 className="w-14 h-14 text-amber-600 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">No In-Store Profile Linked</h2>
            <p className="text-gray-600 text-sm mb-6">Link your account to view transactions.</p>
            <button onClick={() => setShowLinkForm(true)} className="px-5 py-2.5 bg-[#1a3a5c] text-white rounded-xl"><KeyRound className="w-4 h-4 inline mr-2" />Link My Account</button>
          </div>
        )}

        {/* Link form */}
        {!hasLinkedProfile && showLinkForm && (
          <div className="bg-white rounded-xl p-6 max-w-md mx-auto">
            <button onClick={() => setShowLinkForm(false)} className="text-gray-500 text-sm mb-4 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="text-center mb-6">
              <KeyRound className="w-12 h-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-gray-800">Link Store Profile</h2>
            </div>
            {linkError && <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-4 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{linkError}</div>}
            {linkSuccess && <div className="bg-green-50 border border-green-200 text-green-600 px-3 py-2 rounded-lg mb-4 text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" />{linkSuccess}</div>}
            <form onSubmit={handleLinkAccount} className="space-y-4">
              <input type="text" value={linkCode} onChange={(e) => setLinkCode(e.target.value.toUpperCase())} placeholder="ABCD1234" maxLength={10} className="w-full px-4 py-3 border rounded-xl text-center text-xl tracking-widest font-mono text-gray-900" autoFocus />
              <button type="submit" disabled={linkLoading || !linkCode} className="w-full py-3 bg-[#1a3a5c] text-white font-semibold rounded-xl disabled:opacity-50">
                {linkLoading ? <Loader2 className="w-5 h-5 animate-spin inline" /> : 'Link Account'}
              </button>
            </form>
          </div>
        )}

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm flex items-center gap-3"><AlertCircle className="w-5 h-5" />{error}</div>}

        {hasLinkedProfile && invoices.length === 0 && !loading && (
          <div className="bg-white rounded-xl p-8 text-center">
            <FileText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">No Transactions</h2>
            <p className="text-gray-600 text-sm">{message || "Visit our store to get started."}</p>
          </div>
        )}

        {loading && hasLinkedProfile && (
          <div className="bg-white rounded-xl p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#1a3a5c] mx-auto mb-4" />
            <p className="text-gray-600">Loading transactions...</p>
          </div>
        )}

        {/* Invoice Table (Desktop) */}
        {!loading && hasLinkedProfile && invoices.length > 0 && (
          <>
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-sm font-medium text-gray-500">
                    <th className="px-4 py-4">Invoice #</th>
                    <th className="px-4 py-4">Date</th>
                    <th className="px-4 py-4">Customer</th>
                    <th className="px-4 py-4">Transactions</th>
                    <th className="px-4 py-4">Status</th>
                    <th className="px-4 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoices.map((invoice, idx) => {
                    const { received, paid } = getTransactionSummary(invoice);
                    return (
                      <tr key={invoice.invoice_number || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-blue-600 font-medium">{invoice.invoice_number}</td>
                        <td className="px-4 py-4 text-gray-600 text-sm">{formatDate(invoice.date)}</td>
                        <td className="px-4 py-4 text-gray-800 text-sm">{invoice.customer_snapshot?.name || '-'}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {received.map((r, i) => (
                              <span key={`r${i}`} className="px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-600">+{getAmount(r, 'received').toLocaleString()} {r.currency || r.currency_code}</span>
                            ))}
                            {paid.map((p, i) => (
                              <span key={`p${i}`} className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600">-{getAmount(p, 'paid').toLocaleString()} {p.currency || p.currency_code}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${invoice.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{invoice.status}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1">
                            <button onClick={() => setSelectedInvoice(invoice)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Eye className="w-4 h-4" /></button>
                            <button onClick={() => handleDownloadPDF(invoice)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Download className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {invoices.map((invoice, idx) => {
                const { received, paid } = getTransactionSummary(invoice);
                return (
                  <div key={invoice.invoice_number || idx} className="bg-white rounded-xl p-4 shadow">
                    <div className="flex justify-between mb-3">
                      <div>
                        <span className="text-blue-600 font-semibold">{invoice.invoice_number}</span>
                        <div className="text-gray-500 text-sm mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(invoice.date)}</div>
                      </div>
                      <span className={`h-fit px-2.5 py-1 rounded-full text-xs font-medium ${invoice.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{invoice.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {received.map((r, i) => <span key={`r${i}`} className="px-2 py-1 rounded text-xs font-medium bg-green-50 text-green-600 border border-green-200">+{getAmount(r, 'received').toLocaleString()} {r.currency || r.currency_code}</span>)}
                      {paid.map((p, i) => <span key={`p${i}`} className="px-2 py-1 rounded text-xs font-medium bg-red-50 text-red-600 border border-red-200">-{getAmount(p, 'paid').toLocaleString()} {p.currency || p.currency_code}</span>)}
                    </div>
                    <div className="flex gap-2 pt-3 border-t">
                      <button onClick={() => setSelectedInvoice(invoice)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2"><Eye className="w-4 h-4" />View</button>
                      <button onClick={() => handleDownloadPDF(invoice)} className="flex-1 py-2 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"><Download className="w-4 h-4" />PDF</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {hasLinkedProfile && <div className="mt-6 p-3 bg-white/10 rounded-xl border border-white/20"><p className="text-blue-100/80 text-sm">Customer ID: <span className="font-mono text-white font-medium">{user.customer_id}</span></p></div>}
      </div>

      {/* Receipt Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Receipt - {selectedInvoice.invoice_number}</h3>
              <button onClick={() => setSelectedInvoice(null)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-4 sm:p-6 text-[#1a3a5c]">
              <div className="text-center mb-4">
                <img src="https://customer-assets.emergentagent.com/job_1c80a367-1819-4b8f-a04a-b9ee904ccfaa/artifacts/y1bmcq41_logo.png" alt="FonderX" className="h-10 mx-auto" />
              </div>
              <div className="text-center text-xs mb-4">
                <p className="font-medium">FonderX Money Exchange Inc.</p>
                <p>Unit 2 - 723 12th St., New Westminster, V3M 4J8</p>
                <p>(778) 759-0400</p>
              </div>

              <div className="flex justify-between py-3 border-y-4 border-double border-[#1a3a5c] text-xs">
                <div>
                  <p className="font-bold">{selectedInvoice.invoice_number}</p>
                  <p>{selectedInvoice.teller_name || 'Staff'}</p>
                  <p>{selectedInvoice.customer_snapshot?.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{formatDate(selectedInvoice.date)}</p>
                  <p>{formatTime(selectedInvoice.date)}</p>
                  <p className="font-bold">{selectedInvoice.customer_snapshot?.customer_id}</p>
                </div>
              </div>

              {/* Transaction rows */}
              <div className="py-4 text-sm">
                {selectedInvoice.rows?.filter(r => getAmount(r, 'received') > 0).map((row, i) => (
                  <div key={`r${i}`} className="border-t-2 border-[#1a3a5c] pt-4 mb-4">
                    <p className="font-bold text-base">Bought from customer</p>
                    <p className="text-right font-bold text-lg my-2">({getAmount(row, 'received').toLocaleString()} {row.currency || row.currency_code})</p>
                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                      <span>Rate {row.rate || 'N/A'}</span>
                      <span>Fee {row.fee || '0.00'}</span>
                    </div>
                    <p className="text-xs">Bought from customer {getAmount(row, 'received').toLocaleString()} {getCurrencyName(row.currency || row.currency_code)} {getPaymentMethod(row)}</p>
                  </div>
                ))}
                {selectedInvoice.rows?.filter(r => getAmount(r, 'paid') > 0).map((row, i) => (
                  <div key={`p${i}`} className="border-t-2 border-[#1a3a5c] pt-4 mb-4">
                    <p className="font-bold text-base">Paid to customer</p>
                    <p className="text-right font-bold text-lg my-2">({getAmount(row, 'paid').toLocaleString()} {row.currency || row.currency_code})</p>
                    <p className="text-xs">Paid to customer {getAmount(row, 'paid').toLocaleString()} {getCurrencyName(row.currency || row.currency_code)} {getPaymentMethod(row)}</p>
                  </div>
                ))}
              </div>

              <div className="text-center pt-4 border-t">
                <span className={`px-4 py-1 rounded-full text-sm font-medium ${selectedInvoice.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{selectedInvoice.status}</span>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-4 py-3 flex gap-3">
              <button onClick={() => setSelectedInvoice(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">Close</button>
              <button onClick={() => handleDownloadPDF(selectedInvoice)} className="flex-1 py-2 bg-[#1a3a5c] text-white rounded-lg flex items-center justify-center gap-2"><Download className="w-4 h-4" />PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
